import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useApplyPortfolio } from "@/hooks/usePortfolioBuilder";
import { usePortfolioBuilderStore } from "@/stores/portfolioBuilderStore";
import { useCftcGate } from "@/hooks/useCftcGate";
import { useTenant } from "@/hooks/useTenant";
import { FirstTradeDisclosureModal } from "@/components/trade/FirstTradeDisclosureModal";
import { isTenantGateReady } from "@/components/tenant/TenantContextProvider";
import type { StrategyAllocationResponse } from "@/types/portfolio-builder.types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  strategies: StrategyAllocationResponse[];
  totalEquity: number;
}

function formatCurrency(n: number): string {
  return "$" + n.toLocaleString("en-US");
}

export function ApplyConfirmationModal({
  isOpen,
  onClose,
  strategies,
  totalEquity,
}: Props) {
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const { mutate: apply, isPending, error } = useApplyPortfolio();
  const reset = usePortfolioBuilderStore((s) => s.reset);
  // SFWL-017: CFTC §1.55(b) acknowledgement gate, shared with
  // AllocationModal and PaperTradingPanel via the same tenant+user
  // localStorage key. Gate only here — no disclosure copy or attribution UI
  // (that's #92's scope).
  const { runGated, reset: resetGate, modalProps } = useCftcGate();
  // SFWL-017 Codex P1 fix: fail closed on submit while tenant resolution is
  // "pending" or "failed" — see AllocationModal.tsx for the full rationale.
  const { resolution } = useTenant();
  const tenantGateReady = isTenantGateReady(resolution);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // SFWL-017 (P3 review fix): pendingAction lives in useCftcGate's own
  // state, keyed to this component instance, not to `isOpen` — this modal
  // stays mounted (hooks run every render) even while it renders `null`, so
  // a stash left behind by a parent-driven close would otherwise survive
  // and auto-open the disclosure the next time this modal opens, before the
  // user has submitted anything this time around.
  useEffect(() => {
    if (!isOpen) {
      resetGate();
    }
  }, [isOpen, resetGate]);

  if (!mounted || !isOpen) return null;

  // SFWL-017 (P3 review fix): the disclosure has no focus trap, so the
  // controls behind it stay keyboard-reachable (Shift+Tab) while open —
  // freeze them so a user can't change anything between stash time and
  // acknowledge time.
  const disclosureOpen = modalProps.open;

  const performApply = () => {
    apply(
      {
        allocations: strategies.map((s) => ({
          strategyId: s.strategyId,
          units: s.units,
          amount: s.allocation,
        })),
      },
      {
        onSuccess: () => {
          reset();
          navigate("/portfolio");
        },
      }
    );
  };

  const handleApply = () => {
    // Defense in depth alongside the button's `disabled` — mirrors
    // AllocationModal.tsx's `if (!canSubmit) return;` guard.
    if (isPending || disclosureOpen || !tenantGateReady) return;
    runGated(performApply);
  };

  // SFWL-017: backdrop click has no `disabled` attribute to lean on (it's a
  // div, not a button) — guard the handler itself so it can't close this
  // modal (and drop the pending apply) while the disclosure is up.
  const handleCancel = () => {
    if (disclosureOpen) return;
    resetGate();
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <FirstTradeDisclosureModal {...modalProps} />
      <div className="fixed inset-0 bg-black/60" onClick={handleCancel} />
      <div className="relative z-10 mx-4 w-full max-w-md rounded-xl border border-gray-800 bg-gray-900 p-6">
        <h2 className="text-lg font-semibold">Confirm Portfolio Allocation</h2>
        <p className="mt-1 text-sm text-gray-400">
          This will create {strategies.length} allocation
          {strategies.length !== 1 ? "s" : ""} totaling {formatCurrency(totalEquity)}.
        </p>

        <div className="mt-4 space-y-2">
          {strategies.map((s) => (
            <div
              key={s.strategyId}
              className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm"
            >
              <span>{s.name}</span>
              <span className="text-blue-400">
                {s.units} unit{s.units !== 1 ? "s" : ""} · {formatCurrency(s.allocation)}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm font-medium">
          <span>Total</span>
          <span className="text-blue-400">{formatCurrency(totalEquity)}</span>
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-400">
            {error instanceof Error ? error.message : "Failed to apply portfolio"}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={handleCancel}
            disabled={isPending || disclosureOpen}
            className="flex-1 rounded-lg border border-gray-700 py-2.5 text-sm text-gray-300 hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={isPending || disclosureOpen || !tenantGateReady}
            className="flex-1 rounded-lg bg-blue-600 py-2.5 text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
          >
            {isPending ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Allocating...
              </span>
            ) : (
              "Confirm & Allocate"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
